import { useQuery } from '@apollo/client'
import { Chip, Divider, Typography } from '@mui/material'
import { GetCandidateRecruitingPreferences } from 'candidate/graphql/recruitingPreferences.gql'
import { candidateRouteConfig } from 'candidate/routes'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as EmptyPreferences } from 'shared/assets/new_empty_preferences.svg'
import DashboardCard from 'shared/components/DashboardCard'
import { getPxAsRem } from 'shared/utils'
import {
    DashboardCardRow,
    DashboardCardRowTitle,
    DashboardCardSkeleton,
    EmptyStateContainer,
} from './styled'

import type {
    CandidateMeQueryQuery,
    GetCandidateRecruitingPreferencesQuery,
} from '__generated__/graphql'

interface Props {
    candidate: CandidateMeQueryQuery['candidateMe']
}

const PreferencesCard: React.FC<Props> = ({ candidate }) => {
    const { data, loading } = useQuery(GetCandidateRecruitingPreferences, {
        fetchPolicy: 'network-only',
        variables: { id: candidate.user!.id },
        skip: !candidate?.user?.id,
    })
    const navigate = useNavigate()

    const arePreferencesEmpty = (
        candidate: GetCandidateRecruitingPreferencesQuery['candidate']
    ) =>
        !candidate ||
        ((candidate.industryPreferences ?? []).length === 0 &&
            (candidate.locationPreferences ?? []).length === 0 &&
            (candidate.sectorPreferences ?? []).length === 0 &&
            !candidate.startDate)

    const handleCardClick = () => {
        navigate(candidateRouteConfig.preferences.path)
    }

    if (loading)
        return (
            <DashboardCard title="Preferences">
                <DashboardCardSkeleton />
                <Divider />
                <DashboardCardSkeleton />
                <Divider />
                <DashboardCardSkeleton />
                <Divider />
                <DashboardCardSkeleton />
            </DashboardCard>
        )

    if (!data?.candidate || arePreferencesEmpty(data.candidate)) {
        return (
            <DashboardCard title="Preferences" onClick={handleCardClick}>
                <EmptyStateContainer>
                    <EmptyPreferences />
                    <Typography variant="body1">
                        You currently have no filled out recruiting preferences.
                    </Typography>
                </EmptyStateContainer>
            </DashboardCard>
        )
    }

    const industryPreferences = data.candidate.industryPreferences ?? []
    const locationPreferences = data.candidate.locationPreferences ?? []
    const sectorPreferences = data.candidate.sectorPreferences ?? []

    return (
        <DashboardCard title="Preferences" onClick={handleCardClick}>
            <DashboardCardRow>
                <DashboardCardRowTitle variant="body1">
                    Industries
                </DashboardCardRowTitle>
                <Typography component="div" variant="body1">
                    {industryPreferences?.[0]?.preferenceIndustry.value || '-'}
                </Typography>
                {industryPreferences.length > 1 && (
                    <Chip
                        size="small"
                        label={`+${industryPreferences.length - 1}`}
                        sx={{ marginLeft: getPxAsRem(4) }}
                        title={`${industryPreferences.map((pref) => pref.preferenceIndustry.value).join(', ')}`}
                    />
                )}
            </DashboardCardRow>
            <Divider />
            <DashboardCardRow>
                <DashboardCardRowTitle variant="body1">
                    Sectors
                </DashboardCardRowTitle>
                <Typography component="div" variant="body1">
                    {sectorPreferences?.[0]?.sector.value || '-'}
                </Typography>
                {sectorPreferences.length > 1 && (
                    <Chip
                        size="small"
                        label={`+${sectorPreferences.length - 1}`}
                        sx={{ marginLeft: getPxAsRem(4) }}
                        title={`${sectorPreferences.map((pref) => pref.sector.value).join(', ')}`}
                    />
                )}
            </DashboardCardRow>
            <Divider />
            <DashboardCardRow>
                <DashboardCardRowTitle variant="body1">
                    Locations
                </DashboardCardRowTitle>
                <Typography component="div" variant="body1">
                    {locationPreferences?.[0]?.preferenceLocation.value || '-'}
                </Typography>
                {locationPreferences.length > 1 && (
                    <Chip
                        size="small"
                        label={`+${locationPreferences.length - 1}`}
                        sx={{ marginLeft: getPxAsRem(4) }}
                        title={`${locationPreferences.map((pref) => pref.preferenceLocation.value).join(', ')}`}
                    />
                )}
            </DashboardCardRow>
            <Divider />
            <DashboardCardRow>
                <DashboardCardRowTitle variant="body1">
                    Start Date
                </DashboardCardRowTitle>
                <Typography variant="body1">
                    {data.candidate.startDate?.[0]?.value || '-'}
                    {data?.candidate?.startDate &&
                        data.candidate.startDate.length > 1 && (
                            <Chip
                                size="small"
                                label={`+${data.candidate.startDate.length - 1}`}
                                sx={{ marginLeft: getPxAsRem(4) }}
                                title={`${data.candidate?.startDate?.map((pref) => pref?.value).join(', ')}`}
                            />
                        )}
                </Typography>
            </DashboardCardRow>
        </DashboardCard>
    )
}

export default PreferencesCard
